import React, { useState, useEffect } from 'react';
import {
  Calendar,
  User,
  CheckCircle,
  Clock,
  Dumbbell,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api.service';
import { Heat } from '../types/heat.types';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Separator } from '../components/ui/separator';

const ParticipantDashboard: React.FC = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState('events');
  const [events, setEvents] = useState<any[]>([]);
  const [heats, setHeats] = useState<Heat[]>([]);
  const [heatsLoading, setHeatsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form pour modification infos
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get('/users/me/events');

      // Mapper les données pour compatibilité avec l'affichage
      const mappedEvents = response.data.map((event: any) => ({
        ...event,
        date: event.date_start // Pour compatibilité avec getEventStatus
      }));

      setEvents(mappedEvents);
    } catch (err: any) {
      console.error('Erreur chargement événements:', err);
      setError('Impossible de charger tes événements');
    } finally {
      setLoading(false);
    }
  };

  const loadHeats = async () => {
    try {
      setHeatsLoading(true);
      setError(null);

      // Récupérer mes équipes via teams_participants
      const myTeamsResponse = await apiService.get('/users/me/teams');
      const myTeams = myTeamsResponse.data?.teams || [];

      const allHeats: Heat[] = [];

      // Pour chaque équipe, charger ses heats
      for (const team of myTeams) {
        // Seulement si team créée (paiement confirmé, team_uuid existe)
        if (team.team_uuid) {
          try {
            const heatsResponse = await apiService.getTeamHeats(team.team_uuid);
            const teamHeats = heatsResponse.data.heats || [];
            allHeats.push(...teamHeats);
          } catch (teamErr) {
            console.error(`Erreur chargement heats pour team ${team.team_id}:`, teamErr);
          }
        }
      }

      // Trier les heats par date/heure
      allHeats.sort((a, b) => {
        const dateStrA = a.heat_date ? `${a.heat_date}T${a.start_time}` : `2000-01-01T${a.start_time || '00:00:00'}`;
        const dateStrB = b.heat_date ? `${b.heat_date}T${b.start_time}` : `2000-01-01T${b.start_time || '00:00:00'}`;

        const dateA = new Date(dateStrA);
        const dateB = new Date(dateStrB);

        return dateA.getTime() - dateB.getTime();
      });

      setHeats(allHeats);
    } catch (err: any) {
      console.error('Erreur chargement heats:', err);
      setError('Impossible de charger tes heats');
    } finally {
      setHeatsLoading(false);
    }
  };

  // Charger les heats quand on ouvre l'onglet heats
  useEffect(() => {
    if (currentTab === 'heats' && heats.length === 0) {
      loadHeats();
    }
  }, [currentTab]);

  const handleUpdateInfo = async () => {
    try {
      setError(null);
      setSuccess(null);

      // TODO: Créer un endpoint pour mettre à jour les infos utilisateur
      // await apiService.put(`/api/v1/users/${user?.id}`, formData);

      setSuccess('Informations mises à jour avec succès');
    } catch (err: any) {
      console.error('Erreur mise à jour:', err);
      setError('Impossible de mettre à jour tes informations');
    }
  };

  const getEventStatus = (eventDate: string) => {
    const date = new Date(eventDate);
    const now = new Date();

    if (date > now) {
      return { label: 'À venir', isUpcoming: true };
    } else {
      return { label: 'Passé', isUpcoming: false };
    }
  };

  // Contenu du dashboard
  const dashboardContent = (
    <div className="p-6">
      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="events" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Mes événements
          </TabsTrigger>
          <TabsTrigger value="heats" className="flex items-center gap-2">
            <Dumbbell className="h-4 w-4" />
            Mes Heats
          </TabsTrigger>
          <TabsTrigger value="info" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Mes infos
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Mes événements */}
        <TabsContent value="events">
          {loading ? (
            <p className="text-muted-foreground text-center py-8">
              Chargement...
            </p>
          ) : events.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                  Aucune inscription pour le moment
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Tu n'es inscrit à aucun événement. Parcours les événements disponibles et inscris-toi !
                </p>
                <Button asChild>
                  <a href="/events">Voir les événements</a>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {events.map((event: any) => {
                const status = getEventStatus(event.date);
                return (
                  <Card key={event.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-bold text-foreground">{event.name}</h3>
                        <Badge
                          variant={status.isUpcoming ? 'default' : 'secondary'}
                          className={status.isUpcoming ? 'bg-info/15 text-info' : ''}
                        >
                          {status.isUpcoming ? <Clock className="h-3 w-3 mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                          {status.label}
                        </Badge>
                      </div>

                      <p className="text-sm text-muted-foreground mb-1">
                        {new Date(event.date).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>

                      <p className="text-sm text-muted-foreground mb-3">
                        {event.location}
                      </p>

                      <Separator className="my-3" />

                      <p className="text-sm text-muted-foreground">
                        Ma catégorie : <span className="font-bold text-foreground">{event.my_category || 'Non définie'}</span>
                      </p>

                      {!status.isUpcoming && (
                        <Button size="sm" variant="outline" className="mt-3">
                          Voir mes résultats
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab 2: Mes Heats */}
        <TabsContent value="heats">
          {heatsLoading ? (
            <p className="text-muted-foreground text-center py-8">
              Chargement de tes heats...
            </p>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : heats.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Dumbbell className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                  Aucun heat assigné
                </h3>
                <p className="text-sm text-muted-foreground">
                  Tu n'as pas encore de heats programmés pour tes événements.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Grouper les heats par événement */}
              {Object.entries(
                heats.reduce((acc, heat) => {
                  const eventName = heat.event_name || 'Événement inconnu';
                  if (!acc[eventName]) {
                    acc[eventName] = [];
                  }
                  acc[eventName].push(heat);
                  return acc;
                }, {} as Record<string, Heat[]>)
              ).map(([eventName, eventHeats]) => (
                <div key={eventName}>
                  <h3 className="font-bold text-foreground mb-3">{eventName}</h3>
                  <Card>
                    <CardContent className="p-0">
                      {/* Desktop: Table */}
                      <div className="hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date et Heure</TableHead>
                              <TableHead>WOD</TableHead>
                              <TableHead>Lane</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {eventHeats.map((heat) => {
                              let dateTime = 'Heure non définie';

                              if (heat.start_time) {
                                if (heat.heat_date) {
                                  const dateTimeStr = `${heat.heat_date}T${heat.start_time}`;
                                  dateTime = new Date(dateTimeStr).toLocaleString('fr-FR', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  });
                                } else {
                                  dateTime = heat.start_time;
                                }
                              }

                              return (
                                <TableRow key={heat.heat_id}>
                                  <TableCell className="text-muted-foreground">{dateTime}</TableCell>
                                  <TableCell>{heat.wod_name || 'WOD non défini'}</TableCell>
                                  <TableCell className="font-bold text-foreground">Lane {heat.lane_number}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile: Heat Cards */}
                      <div className="md:hidden p-4 space-y-3">
                        {eventHeats.map((heat) => {
                          let dateTime = 'Heure non définie';

                          if (heat.start_time) {
                            if (heat.heat_date) {
                              const dateTimeStr = `${heat.heat_date}T${heat.start_time}`;
                              dateTime = new Date(dateTimeStr).toLocaleString('fr-FR', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            } else {
                              dateTime = heat.start_time;
                            }
                          }

                          return (
                            <div key={heat.heat_id} className="border-b last:border-b-0 pb-3 last:pb-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold">{heat.wod_name || 'WOD non défini'}</span>
                                <span className="font-bold text-foreground">Lane {heat.lane_number}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">{dateTime}</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab 3: Mes infos */}
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">Informations personnelles</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert variant="success">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => setFormData({
                    first_name: user?.first_name || '',
                    last_name: user?.last_name || '',
                    email: user?.email || ''
                  })}
                >
                  Annuler
                </Button>

                <Button onClick={handleUpdateInfo}>
                  Enregistrer les modifications
                </Button>
              </div>

              <Separator />

              <div>
                <h3 className="font-bold text-foreground mb-3">Informations de compte</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Rôle</p>
                    <p className="font-bold">
                      {user?.role === 'participant' ? 'Participant' : user?.role}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">Membre depuis</p>
                    <p className="font-bold">Date non disponible</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  return dashboardContent;
};

export default ParticipantDashboard;
